import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  deleteUser as deleteUserApi,
  readUser,
  searchUser,
  updateUser as updateUserApi,
  type SearchQuery,
  type UserInDb,
  type UserSearchResult,
  type UserUpdateSuperuser,
} from '@/api';
import { logger } from '@/utils/logger';

export const useAdminUsersStore = defineStore('admin-users', () => {
  // 1. State
  const users = ref<UserInDb[]>([]);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const total = ref<number>(0);
  const currentPage = ref<number>(1);
  const pageSize = ref<number>(10);

  // 2. Getters
  const hasUsers = computed<boolean>(() => {
    return users.value.length > 0;
  });

  const totalPages = computed<number>(() => {
    return Math.ceil(total.value / pageSize.value);
  });

  // 3. Actions
  async function fetchUsers(queryOrPage?: SearchQuery | number, limit = 10): Promise<void> {
    loading.value = true;
    error.value = null;

    let query: SearchQuery;

    // Handle backward compatibility with existing code
    if (typeof queryOrPage === 'number') {
      currentPage.value = queryOrPage;
      pageSize.value = limit;
      query = {
        text: '',
        limit,
        offset: (queryOrPage - 1) * limit,
        sort_key: 'full_name',
        sort_direction: 'asc',
      };
    } else if (queryOrPage) {
      // Use provided query
      query = queryOrPage;

      // Update state based on query
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
      pageSize.value = query.limit || 10;
      currentPage.value =
        query.offset === undefined ? 1 : Math.floor(query.offset / pageSize.value) + 1;
    } else {
      // Default query
      query = {
        text: '',
        limit: pageSize.value,
        offset: (currentPage.value - 1) * pageSize.value,
        sort_key: 'full_name',
        sort_direction: 'asc',
      };
    }

    try {
      const response = await searchUser({ body: query });
      if (!response.data) {
        throw new Error('Failed to fetch users');
      }
      const result: UserSearchResult = response.data;

      users.value = result.results;
      total.value = result.result_count;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to fetch users';
      logger.error('Error fetching users:', error_);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get a specific user by ID
   * @param uid The user ID
   * @returns UserInDB or null if not found
   */
  async function getUser(uid: string): Promise<UserInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await readUser({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to fetch user');
      }
      return response.data;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to fetch user';
      logger.error('Error fetching user:', error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update a user
   * @param uid The user ID
   * @param data UserUpdateSuperuser object with fields to update
   * @returns UserInDB or null if update failed
   */
  async function updateUser(uid: string, data: UserUpdateSuperuser): Promise<UserInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await updateUserApi({
        path: { uid },
        body: data,
      });
      if (!response.data) {
        throw new Error('Failed to update user');
      }
      const result = response.data;
      // Refresh the list after update
      await fetchUsers(currentPage.value, pageSize.value);
      return result;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to update user';
      logger.error('Error updating user:', error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a user
   * @param uid The user ID
   * @returns boolean indicating success
   */
  async function deleteUser(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      await deleteUserApi({ path: { user_uid: uid } });
      // Refresh the list after deletion
      await fetchUsers(currentPage.value, pageSize.value);
      return true;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to delete user';
      logger.error('Error deleting user:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Deactivate a user (sets is_active to false)
   * @param uid The user ID
   * @returns UserInDB or null if update failed
   */
  async function deactivateUser(uid: string): Promise<UserInDb | null> {
    return updateUser(uid, { is_active: false });
  }

  /**
   * Activate a user (sets is_active to true)
   * @param uid The user ID
   * @returns UserInDB or null if update failed
   */
  async function activateUser(uid: string): Promise<UserInDb | null> {
    return updateUser(uid, { is_active: true });
  }

  return {
    // State
    users,
    loading,
    error,
    total,
    currentPage,
    pageSize,

    // Getters
    hasUsers,
    totalPages,

    // Actions
    fetchUsers,
    getUser,
    updateUser,
    deleteUser,
    deactivateUser,
    activateUser,
  };
});
